import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, unwrap } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Field, RootError, Section, Switch, Textarea } from '@/components/ui/form-controls';
import { Input } from '@/components/ui/input';
import { mapProblem } from '@/lib/forms';
import { asRecord, parseJson, prettyJson } from '@/lib/json';
import { useTenantMutation, type TenantDto } from './api';
import { BOOL_CAPS, CAPABILITIES, capsFromJson, capsToJson, parseProviderName, providerOptions, validateCaps, validateModelPolicy, type CapsState } from './caps';

export function CapsTab({ tenant }: { tenant: TenantDto }) {
  const { t } = useTranslation();
  const initial = useMemo(() => capsFromJson(tenant.caps), [tenant.caps]);
  const [state, setState] = useState<CapsState>(initial);
  const [error, setError] = useState<string | null>(null);
  const [newProvider, setNewProvider] = useState('');
  const addProvider = () => {
    const name = parseProviderName(newProvider);
    if (!name) {
      setError(t('admin.caps.invalidProvider'));
      return;
    }
    setError(null);
    setNewProvider('');
    setState((s) => (s.allowedProviders.includes(name) ? s : { ...s, allowedProviders: [...s.allowedProviders, name] }));
  };
  const save = useTenantMutation(
    tenant.id,
    async (caps: Record<string, unknown>) =>
      unwrap(
        await api.PATCH('/v1/tenants/{id}', {
          params: { path: { id: tenant.id } },
          body: { name: null, timezone: null, locales: null, status: null, residency: null, caps: caps as never, modelPolicy: null },
        }),
      ),
    t('admin.common.saved'),
  );

  const submit = async () => {
    const v = validateCaps(state);
    if (v) {
      setError(t(v));
      return;
    }
    setError(null);
    try {
      await save.mutateAsync(capsToJson(state));
    } catch (err) {
      setError(mapProblem(err, { fields: [] }).rootMessage);
    }
  };

  return (
    <Section title={t('admin.tenantTabs.caps')} description={t('admin.caps.description')}>
      <div className="grid max-w-xl gap-3">
        {BOOL_CAPS.map((k) => (
          <Switch
            key={k}
            label={
              <span>
                <span className="font-medium">{t(`admin.caps.${k}`)}</span>
                <span className="block text-xs text-muted-foreground">{t(`admin.caps.${k}Hint`)}</span>
              </span>
            }
            checked={state.flags[k] ?? false}
            onChange={(e) => setState((s) => ({ ...s, flags: { ...s.flags, [k]: e.target.checked } }))}
          />
        ))}
        <fieldset className="flex flex-wrap gap-3">
          <legend className="mb-1 text-xs font-medium">{t('admin.caps.allowedProviders')}</legend>
          {providerOptions(state.allowedProviders).map((p) => (
            <label key={p} className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={state.allowedProviders.includes(p)}
                onChange={(e) =>
                  setState((s) => ({
                    ...s,
                    allowedProviders: e.target.checked ? [...s.allowedProviders, p] : s.allowedProviders.filter((x) => x !== p),
                  }))
                }
              />
              <span className="font-mono text-xs">{p}</span>
            </label>
          ))}
          <div className="flex w-full items-center gap-2">
            <Input
              aria-label={t('admin.caps.addProvider')}
              placeholder={t('admin.caps.addProviderPlaceholder')}
              className="h-8 max-w-56 font-mono text-xs"
              value={newProvider}
              onChange={(e) => setNewProvider(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addProvider();
                }
              }}
            />
            <Button type="button" variant="outline" size="sm" onClick={addProvider}>
              {t('admin.caps.addProvider')}
            </Button>
          </div>
        </fieldset>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('admin.caps.maxAgents')} htmlFor="cap-ma">
            <Input id="cap-ma" inputMode="numeric" value={state.maxAgents} onChange={(e) => setState((s) => ({ ...s, maxAgents: e.target.value }))} />
          </Field>
          <Field label={t('admin.caps.maxRunsPerDay')} htmlFor="cap-mr">
            <Input id="cap-mr" inputMode="numeric" value={state.maxRunsPerDay} onChange={(e) => setState((s) => ({ ...s, maxRunsPerDay: e.target.value }))} />
          </Field>
        </div>
        <RootError message={error ?? undefined} />
        <div>
          <Button onClick={() => void submit()} disabled={save.isPending}>
            {t('admin.common.save')}
          </Button>
        </div>
      </div>
    </Section>
  );
}

export function ModelPolicyTab({ tenant }: { tenant: TenantDto }) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => prettyJson(asRecord(tenant.modelPolicy)));
  const [error, setError] = useState<string | null>(null);
  const save = useTenantMutation(
    tenant.id,
    async (policy: Record<string, unknown>) =>
      unwrap(
        await api.PATCH('/v1/tenants/{id}', {
          params: { path: { id: tenant.id } },
          body: { name: null, timezone: null, locales: null, status: null, residency: null, caps: null, modelPolicy: policy as never },
        }),
      ),
    t('admin.common.saved'),
  );

  const submit = async () => {
    const parsed = parseJson(text);
    if (!parsed.ok) {
      setError(`${t('admin.validation.json')}: ${parsed.error}`);
      return;
    }
    const v = validateModelPolicy(parsed.value);
    if (v) {
      setError(t(v, { capabilities: CAPABILITIES.join(', ') }));
      return;
    }
    setError(null);
    try {
      await save.mutateAsync(asRecord(parsed.value));
    } catch (err) {
      setError(mapProblem(err, { fields: [] }).rootMessage);
    }
  };

  return (
    <Section title={t('admin.tenantTabs.modelPolicy')} description={t('admin.modelPolicy.description')}>
      <Field label={t('admin.modelPolicy.json')} htmlFor="mp-json" hint={t('admin.modelPolicy.hint', { capabilities: CAPABILITIES.join(', ') })}>
        <Textarea id="mp-json" rows={14} className="font-mono text-xs" value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} />
      </Field>
      <RootError message={error ?? undefined} />
      <div>
        <Button onClick={() => void submit()} disabled={save.isPending}>
          {t('admin.common.save')}
        </Button>
      </div>
    </Section>
  );
}
